import { getOwner } from '@ember/application';
import { datadogRum } from '@datadog/browser-rum';
import { isArray } from '@ember/array';
import { pluralize } from '@warp-drive/utilities/string';
import { service } from '@ember/service';
import ENV from 'tio-employee/config/environment';
import JSONAPIAdapter from '@ember-data/adapter/json-api';
import type Model from '@ember-data/model';
import type SessionService from '../services/session';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type SessionContextService from 'tio-employee/services/session-context';
// @ts-expect-error: https://github.com/emberjs/data/issues/10086#issue-3263836397
import type { Snapshot } from '@ember-data/legacy-compat/legacy-network-handler/snapshot';
import type { ModelSchema } from '@ember-data/store/types';
import type { RequestData } from '@ember-data/adapter/rest';
import { timeZone } from 'tio-common/utils/date/time-zone';
import { generateRequestId } from 'tio-common/utils/request-id';

type Headers = {
  'x-api-key': string;
  'tio-auth-token'?: string;
  'Time-Zone': string;
  'X-Request-Id': string;
};

/**
 * An adapter for all models within the application that are not overridden by
 * more specific adapters. This adapter receives requests from the store and
 * translates those into the appropriate actions to take against the API.
 *
 * @memberof adapters
 */
class ApplicationAdapter extends JSONAPIAdapter {
  // RESTAdapter's published types omit BuildURLMixin's methods
  // (https://github.com/emberjs/data/issues/10086). Declare permissive
  // signatures here so `this.buildURL(...)` and the urlFor* overrides
  // typecheck without per-call `@ts-expect-error`. Same pattern as
  // tio-employee/app/adapters/search.ts.
  declare buildURL: (...args: unknown[]) => string;

  @service declare session: SessionService;
  @service declare sessionContext: SessionContextService;

  host = ENV.apiHost;

  // @ts-expect-error stop using adapters
  get headers() {
    const headers: Headers = {
      'x-api-key': ENV.apiKey,
      'Time-Zone': timeZone(),
      'X-Request-Id': generateRequestId(),
    };

    if (this.session.isAuthenticated) {
      headers['tio-auth-token'] = `${this.session.data.authenticated?.access_token}`;
    }

    return headers;
  }

  /**
    Override urlForUpdateRecord to support additional forms of json-api spec
    for saving records.

    When doing `model.save({adapterOptions: { updateRelationship: 'people' }});` the following will be generated.

    ```
    PATCH /articles/1/relationships/author HTTP/1.1
    Content-Type: application/vnd.api+json
    Accept: application/vnd.api+json

    {
      "data": { "type": "people", "id": "12" }
    }
    ```
  */
  // @ts-expect-error: https://github.com/emberjs/data/issues/10086#issue-3263836397
  urlForUpdateRecord(id: string, modelName: string, snapshot: Snapshot) {
    const originalEndpoint = super.urlForUpdateRecord(id, modelName, snapshot);

    const { adapterOptions } = snapshot;
    if (adapterOptions && Array.isArray(adapterOptions.createHasMany)) {
      const hasManyModel = adapterOptions?.createHasMany?.[0]?.constructor?.modelName;

      return `${originalEndpoint}/relationships/${pluralize(hasManyModel)}`;
    }

    if (adapterOptions?.updateRelationship) {
      return `${originalEndpoint}/relationships/${adapterOptions?.updateRelationship}`;
    }
    return originalEndpoint;
  }
  // @ts-expect-error: https://github.com/emberjs/data/issues/10086#issue-3263836397
  urlForDeleteRecord(id: string, modelName: string, snapshot: Snapshot) {
    const originalEndpoint = super.urlForDeleteRecord(id, modelName, snapshot);
    const { adapterOptions } = snapshot;

    if (adapterOptions && isArray(adapterOptions.destroyHasMany)) {
      const hasManyModel = adapterOptions?.destroyHasMany?.[0]?.constructor?.modelName;

      return `${originalEndpoint}/relationships/${pluralize(hasManyModel)}`;
    }

    return originalEndpoint;
  }
  // @ts-expect-error: https://github.com/emberjs/data/issues/10086#issue-3263836397
  updateRecord(store: StoreService, type: ModelSchema, snapshot: Snapshot) {
    const { adapterOptions } = snapshot;
    if (adapterOptions && isArray(adapterOptions.createHasMany)) {
      const data = {};
      const serializer = store.serializerFor(type.modelName);
      // @ts-expect-error - Ember Data serializer possibly null after rollback
      serializer.serializeIntoHash(data, type, snapshot);

      const id = snapshot.id;
      const url = this.buildURL(type.modelName, id, snapshot, 'updateRecord');

      return this.ajax(url, 'POST', { data: data }).then(() => {
        return null;
      });
    }
    if (adapterOptions && isArray(adapterOptions.destroyHasMany)) {
      const data = {};
      const serializer = store.serializerFor(type.modelName);
      // @ts-expect-error - Ember Data serializer possibly null after rollback
      serializer.serializeIntoHash(data, type, snapshot);

      const id = snapshot.id;
      const url = this.buildURL(type.modelName, id, snapshot, 'deleteRecord');

      return this.ajax(url, 'DELETE', { data: data }).then(() => {
        return null;
      });
    }

    return super.updateRecord(store, type, snapshot);
  }

  /**
   * This method is called for every response that the adapter receives from the
   * API. If the response has a 401 or 403 status code it invalidates the session (see
   *
   * @param {Number} status The response status as received from the API
   * @param  {Object} headers HTTP headers as received from the API
   * @param {Any} payload The response body as received from the API
   * @param {Object} requestData the original request information
   * @protected
   */
  handleResponse(
    status: number,
    headers: Record<string, string>,
    payload: Record<string, unknown>,
    requestData: RequestData
  ) {
    this.ensureResponseAuthorized(status, headers, payload, requestData);

    this.renewSessionIfActive();

    return super.handleResponse(status, headers, payload, requestData);
  }

  /**
   * The default implementation for handleResponse.
   * If the response has a 401 status code it invalidates the session.
   * If the response has a 400 or 403 status code, it shows a toast notification instead of logging out.
   * Override this method if you want custom invalidation logic for incoming responses.
   *
   * @param {Number} status The response status as received from the API
   * @param  {Object} headers HTTP headers as received from the API
   * @param {Any} payload The response body as received from the API
   * @param {Object} requestData the original request information
   */
  ensureResponseAuthorized(
    status: number,
    headers: unknown,
    payload: unknown,
    requestData: unknown
  ) {
    if (status === 401 && this.session.isAuthenticated) {
      // Only logout for 401 (unauthorized/expired token)
      console.error('ensureResponseAuthorized caught 401 error', {
        status,
        headers,
        payload,
        requestData,
        userId: this.sessionContext.user.id,
      });

      datadogRum.addError(
        `ensureResponseAuthorized caught error status ${status} for user ${this.sessionContext.user.id}`,
        {
          status,
          headers,
          payload,
          requestData,
          userId: this.sessionContext.user.id,
        }
      );
      this.sessionContext.logout();
    } else if ([400, 403, 422].includes(status) && this.session.isAuthenticated) {
      // Show toast notification for 400 (bad request), 403 (forbidden), and 422 (unprocessable entity) errors
      console.warn('ensureResponseAuthorized caught non-fatal error', {
        status,
        headers,
        payload,
        requestData,
        userId: this.sessionContext.user.id,
      });

      // Try to extract error message from payload
      let errorMessage =
        'An error occurred. Please try again or contact support if the problem persists.';

      if (payload && typeof payload === 'object') {
        const payloadObj = payload as Record<string, unknown>;
        if (payloadObj.errors && Array.isArray(payloadObj.errors) && payloadObj.errors.length > 0) {
          const firstError = payloadObj.errors[0] as Record<string, unknown>;
          errorMessage = (firstError.detail || firstError.title || errorMessage) as string;
        } else if (payloadObj.error && typeof payloadObj.error === 'string') {
          errorMessage = payloadObj.error;
        } else if (payloadObj.message && typeof payloadObj.message === 'string') {
          errorMessage = payloadObj.message;
        }
      }

      // Show appropriate message based on status code
      if (status === 400) {
        errorMessage = `Bad request: ${errorMessage}`;
      } else if (status === 403) {
        errorMessage = `Access denied: ${errorMessage}`;
      } else if (status === 422) {
        // For validation errors like duplicates, show the error message directly without prefix
        // The API should provide a clear message like "Email already exists" or "Payroll ID already in use"
      }

      // Use the notifications service to show a toast
      // Note: This assumes the notifications service is available
      try {
        const owner = getOwner(this);
        const notificationsService = owner?.lookup('service:notifications');
        if (notificationsService && typeof notificationsService.add === 'function') {
          notificationsService.add(errorMessage, { appearance: 'error' });
        } else {
          // Fallback to console if notifications service is not available
          console.error('Error notification:', errorMessage);
        }
      } catch (e) {
        console.error('Failed to show error notification:', e, 'Original error:', errorMessage);
      }

      datadogRum.addError(
        `ensureResponseAuthorized caught non-fatal error status ${status} for user ${this.sessionContext.user.id}`,
        {
          status,
          headers,
          payload,
          requestData,
          userId: this.sessionContext.user.id,
          errorMessage,
        }
      );
    }
  }

  /**
   * This method is called for every request that the adapter makes to the API.
   * If the session is authenticated and the session has 30 minutes or less left before it expires,
   * the session is renewed.
   *
   */
  renewSessionIfActive() {
    const timeLeft = this.session.expireAt ? this.session.expireAt - Date.now() : 0;
    const thirtyMinutes = 1800000;

    if (this.session.isAuthenticated && timeLeft < thirtyMinutes && timeLeft > 0) {
      try {
        this.session.renew();
      } catch (error) {
        console.error('Failed to renew session:', error);
      }
    }
  }

  /**
   * Submits a non–tuition-assistance (non-TA) scheduling request for coaching.
   *
   * @param {string} scheduleRequestContent - Plain text content of the scheduling request
   * @returns {Promise<boolean>} true if the request succeeded false otherwise
   */
  async submitNonTaRequest(scheduleRequestContent: string): Promise<boolean> {
    const url = `${this.host}/coaching/schedule-loan`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify({ scheduleRequestContent }),
      });

      return response.ok === true;
    } catch (error) {
      console.error('Failed to submit non-TA request:', error);
      // Network-level failure
      return false;
    }
  }

  /**
   * Submits a tuition-assistance (TA) mentoring request for coaching.
   *
   * @param {string} mentorRequestContent - Plain text content of the TA mentoring request
   * @returns {Promise<boolean>} true if the request succeeded, false otherwise
   */
  async submitTasRequest(mentorRequestContent: string): Promise<boolean> {
    const url = `${this.host}/coaching/schedule-tas`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify({ mentorRequestContent }),
      });

      return response.ok === true;
    } catch (error) {
      console.error('Failed to submit TA request:', error);
      // Network-level failure
      return false;
    }
  }

  /**
   * Queries the API for aggregate data that the `owner` has permission to access.
   *
   * @param {Model}  owner The model used to determine the access restrictions for the query.
   *                       Usually the current user
   * @param {object} query The query that defines what data should be returned
   *
   * @return {object} An array of aggregate data objects representing the aggregate data for the
   *                  model specified by `query.model`
   */
  async aggregate(owner: Model, query: Record<string, unknown>) {
    // @ts-expect-error: not sure
    const url = this.buildURL(owner.constructor.modelName, owner.id) + '/aggregate';
    const response = await this.ajax(url, 'GET', { data: query });
    // @ts-expect-error: not sure
    return response.data;
  }
}

export default ApplicationAdapter;
